/**
 * Edit a Binom offer — Name + URL + Country + Group + Affiliate network +
 * Payout (amount + currency + Auto + Upsell). Save writes via
 * PUT /binom/offers/:id. Edits the GLOBAL offer entity (warning shown).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useState } from 'react';

import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { Button } from '@/components/ui/layout/Button';
import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';
import {
  useBinomAffiliateNetworks,
  useBinomCountries,
  useBinomGroups,
  useBinomOffer,
  useUpdateBinomOffer,
  type BinomOfferDetail,
} from '@/hooks/binom';

import BinomOfferEditBody from './body/BinomOfferEditBody';

export interface BinomOfferEditDialogProps {
  offerId: string;
  onClose: () => void;
}

const BinomOfferEditDialog: FC<BinomOfferEditDialogProps> = ({
  offerId,
  onClose,
}) => {
  const offerQuery = useBinomOffer(offerId);
  const update = useUpdateBinomOffer(offerId);
  const groupsQuery = useBinomGroups('offers');
  const countriesQuery = useBinomCountries();
  const networksQuery = useBinomAffiliateNetworks();
  const [form, setForm] = useState<BinomOfferDetail | null>(null);

  useEffect(() => {
    if (offerQuery.data && !form) setForm(offerQuery.data);
  }, [offerQuery.data, form]);

  const groupOptions = (Array.isArray(groupsQuery.data) ? groupsQuery.data : [])
    .map((g) => ({ value: g.uuid, label: g.name }));
  const countryOptions = (Array.isArray(countriesQuery.data) ? countriesQuery.data : [])
    .map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));
  const networkOptions = (Array.isArray(networksQuery.data) ? networksQuery.data : [])
    .map((n) => ({ value: n.id, label: n.name }));

  const save = async () => {
    if (!form) return;
    await update.mutateAsync(form);
    onClose();
  };

  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <DrawerContent variant="center" width="w-[42rem]">
        <DrawerHeader>
          <DrawerTitle>
            Edit offer: {form?.name ?? offerQuery.data?.name ?? offerId}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          {offerQuery.isLoading || !form ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : (
            <BinomOfferEditBody
              form={form}
              onChange={setForm}
              countryOptions={countryOptions}
              groupOptions={groupOptions}
              networkOptions={networkOptions}
            />
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="outline" onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!form || update.isPending}
            loading={update.isPending}
            onClick={save}
          >
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BinomOfferEditDialog;
